import type { BuildState, BuildDerived, BuildWarning } from '@/types';
import { classifyFOC, classifyKE, getShaftDiameterInches } from './calculations';
import { recommendSpine } from './spine';

/**
 * Aggregate all build warnings into one ordered list. Designed for a
 * single-glance "is this build safe + effective?" panel.
 *
 * Severity bands:
 *   info     — informational note, not a problem
 *   caution  — outside ideal, fixable
 *   critical — safety or performance flaw
 *
 * The set of checks is deliberately conservative — only flag what an
 * experienced shop tech would actually push back on.
 */
export function buildWarnings(state: BuildState, derived: BuildDerived): BuildWarning[] {
  const out: BuildWarning[] = [];

  // ---------- Weight ----------
  const w = derived.weight;
  if (w < 300) {
    out.push({
      id: 'weight-too-light',
      level: 'critical',
      title: 'Arrow too light',
      detail: `${w.toFixed(0)} gr is below the 5 gr/lb IBO safety minimum for most modern bows. May void warranty and risk dry-fire damage.`,
      color: 'rose',
    });
  } else if (w < state.bow.drawWeight * 5) {
    out.push({
      id: 'weight-below-ibo-rule',
      level: 'caution',
      title: 'Below 5 gr/lb safety guideline',
      detail: `${w.toFixed(0)} gr is under ${(state.bow.drawWeight * 5).toFixed(0)} gr (5 gr per lb of ${state.bow.drawWeight} lb draw weight) — most bow manufacturers require 5 gr/lb to maintain warranty.`,
      color: 'amber',
    });
  } else if (w > 700) {
    out.push({
      id: 'weight-very-heavy',
      level: 'caution',
      title: 'Very heavy arrow',
      detail: `${w.toFixed(0)} gr will give excellent penetration but expect significant trajectory drop past 30 yards.`,
      color: 'amber',
    });
  }

  // ---------- FOC ----------
  const focZone = classifyFOC(derived.foc);
  if (focZone.color === 'rose') {
    out.push({
      id: 'foc-too-low',
      level: 'critical',
      title: 'FOC too low',
      detail: focZone.detail,
      color: 'rose',
    });
  } else if (focZone.color === 'amber') {
    out.push({
      id: 'foc-low',
      level: 'caution',
      title: 'FOC below hunting standard',
      detail: focZone.detail,
      color: 'amber',
    });
  } else if (focZone.color === 'violet') {
    out.push({
      id: 'foc-ultra-efoc',
      level: 'info',
      title: 'Ultra-EFOC build',
      detail: 'Maximum penetration at the cost of trajectory. Sight in for 20 yards and verify drop at 40+ before hunting.',
      color: 'sky',
    });
  } else {
    out.push({
      id: 'foc-ok',
      level: 'info',
      title: 'FOC in hunting range',
      detail: 'Strong forward weight — predictable flight and penetration.',
      color: 'emerald',
    });
  }

  // ---------- Spine ----------
  const spine = recommendSpine(state);
  if (spine.status === 'stiff') {
    out.push({
      id: 'spine-stiff',
      level: 'caution',
      title: 'Spine likely too stiff',
      detail: spine.message,
      color: 'amber',
    });
  } else if (spine.status === 'weak') {
    out.push({
      id: 'spine-weak',
      level: 'critical',
      title: 'Spine likely too weak',
      detail: spine.message + ' Weak-spine arrows can flex dangerously at release.',
      color: 'rose',
    });
  } else {
    out.push({
      id: 'spine-ok',
      level: 'info',
      title: 'Spine within Easton recommendation',
      detail: spine.message,
      color: 'emerald',
    });
  }

  // ---------- Velocity ----------
  if (state.fps < 200) {
    out.push({
      id: 'velocity-low',
      level: 'caution',
      title: 'Low velocity',
      detail: `${state.fps} fps. Trajectory drop becomes significant past 30 yards. Acceptable for traditional setups.`,
      color: 'amber',
    });
  }
  // Compare to estimated bow speed if user has set bow specs
  const fpsDelta = Math.abs(state.fps - derived.estimatedFps);
  if (state.bow.iboSpeed > 0 && fpsDelta > 25) {
    out.push({
      id: 'velocity-mismatch',
      level: 'info',
      title: 'Measured vs. estimated velocity differs',
      detail: `Estimated ${derived.estimatedFps.toFixed(0)} fps from bow specs, measured ${state.fps} fps. ${state.fps > derived.estimatedFps ? 'Faster than expected' : 'Slower than expected'} — verify chrono or check string condition.`,
      color: 'sky',
    });
  }

  // ---------- KE ----------
  const keClass = classifyKE(derived.ke);
  if (keClass.level === 'insufficient') {
    out.push({
      id: 'ke-insufficient',
      level: 'critical',
      title: 'KE below most state minimums',
      detail: `${derived.ke.toFixed(1)} ft·lb. Most jurisdictions require ≥25 ft·lb for big game. Increase arrow weight or velocity.`,
      color: 'rose',
    });
  }

  // ---------- Stability ----------
  // Static margin in calibers — positive = stable (CG forward of CP).
  if (derived.stabilityCalibers < 30) {
    out.push({
      id: 'stability-low',
      level: 'caution',
      title: 'Low static margin',
      detail: `Only ${derived.stabilityCalibers.toFixed(0)} calibers of stability margin. Add weight forward (heavier point or brass insert) or increase vane area.`,
      color: 'amber',
    });
  } else if (derived.stabilityCalibers > 120) {
    out.push({
      id: 'stability-overstable',
      level: 'info',
      title: 'Very over-stable',
      detail: `${derived.stabilityCalibers.toFixed(0)} calibers — flight will be planted and reliable, with mild over-correction in crosswind.`,
      color: 'sky',
    });
  }

  // ---------- BC ----------
  if (derived.bc < 0.25) {
    out.push({
      id: 'bc-low',
      level: 'caution',
      title: 'Low ballistic coefficient',
      detail: `BC ${derived.bc.toFixed(2)} — expect significant velocity loss at distance. Larger-diameter shaft + lightweight setup.`,
      color: 'amber',
    });
  }

  // ---------- Diameter sanity ----------
  const od = getShaftDiameterInches(state);
  if (od < 0.15) {
    out.push({
      id: 'od-micro',
      level: 'info',
      title: 'Micro-diameter shaft',
      detail: 'Excellent penetration and wind drift, but requires matching micro-diameter components.',
      color: 'sky',
    });
  }

  // Stable sort: critical → caution → info
  const order = { critical: 0, caution: 1, info: 2 };
  return out.sort((a, b) => order[a.level] - order[b.level]);
}
